import * as React from 'react'

import { Box, Button, Icon, Text } from '@factorialco/deprecated-design-system'
import Decimal from 'decimal.js'
import { mapValues, pickBy } from 'lodash'

import { useFormContext } from 'modules/core/hooks/useForm'
import i18n from 'modules/core/lib/i18n'

import type { Form } from 'modules/performance_process/components/score_configuration_weights/ScoreWeightsSidepanel'
import { type MappedQuestionnaires } from 'modules/performance_process/hooks/useMappedQuestionnaires'

import { StrategiesProgressBar } from '../StrategiesProgressBar'

export type WeightValidationStatus = 'not_enough' | 'exceed' | 'ok'
const getWeightValidationStatus = (weight: number): WeightValidationStatus => {
  if (weight === 100) return 'ok'

  return weight < 100 ? 'not_enough' : 'exceed'
}

type Props = {
  questionnaires: MappedQuestionnaires
}

export const StrategyConfigurationsProgress: React.FC<Props> = ({}) => {
  const form = useFormContext<Form>()
  const configurations = Object.values(form.values.configurationByStrategy)
  const autoDistributableStrategies = configurations
    .filter((config) => config.weight === 0)
    .map((config) => config.reviewerStrategy)
  const errorMessages: Record<WeightValidationStatus, string | null> = {
    not_enough: i18n.t('performance_process.score_strategy_weights.weight_errors.not_enough', {
      weight: 100,
    }),
    exceed: i18n.t('performance_process.score_strategy_weights.weight_errors.exceed', {
      weight: 100,
    }),
    ok: null,
  }

  const currentTotal = Decimal.sum(0, ...configurations.map(({ weight }) => weight || 0)).toNumber()
  const validationStatus = getWeightValidationStatus(currentTotal)
  const canDistribute = validationStatus === 'not_enough' && autoDistributableStrategies.length > 0

  const handleClick = () => {
    const necessaryWeight = new Decimal(100)
    const totalWeightDecimal = new Decimal(currentTotal)
    const remaining = necessaryWeight.minus(totalWeightDecimal)
    if (remaining.gt(0)) {
      const amountToDistribute = remaining
        .div(autoDistributableStrategies.length)
        .toDecimalPlaces(1)
        .toNumber()

      const newConfigurations = mapValues(
        pickBy(form.values.configurationByStrategy, (config) => config !== undefined),
        (config) => {
          const isWeightable = autoDistributableStrategies.includes(config.reviewerStrategy)
          return {
            ...config,
            weight: isWeightable ? amountToDistribute : config.weight,
            producesScore: isWeightable
              ? config.producesScore || amountToDistribute > 0
              : config.producesScore,
          }
        }
      )

      form.setFieldValue('configurationByStrategy', newConfigurations)
    }
  }

  return (
    <>
      <Box flexDirection="row" alignItems="center" justifyContent="spaceBetween">
        <Text.Normal size="400" weight="semibold" color="textPrimary">
          {i18n.t('performance_process.score_strategy_weights.total_section_title')}
        </Text.Normal>
        <Button
          type="secondary"
          onClick={handleClick}
          disabled={form.isSubmitting || !canDistribute}
        >
          {i18n.t('performance_process.section_weights.distribute_remaining')}
        </Button>
      </Box>
      <Box>
        <Box flexDirection="row" gap="s8" alignItems="center">
          <StrategiesProgressBar
            status={validationStatus}
            strategyConfigurations={form.values.configurationByStrategy}
            totalProgress={currentTotal}
          />
          <Icon.Large
            icon={validationStatus === 'ok' ? 'check' : 'alert'}
            color={validationStatus === 'ok' ? 'viridian1000' : 'radicalRed1000'}
          />
        </Box>
        <Box minHeight="s20">
          <Text.Normal size="200" weight="normal" color="radicalRed1000">
            {validationStatus !== 'ok' && errorMessages[validationStatus]}
          </Text.Normal>
        </Box>
      </Box>
    </>
  )
}
